import {
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import { useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useEffect } from "react";
import { useLazyGetVendorShipmentCountsQuery } from "store/services/reportApi";
import ExportVendorShipmentCount from "./ExportVendorShipmentCountReport";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { useSearchParams } from "react-router-dom";
import HighlightedText from "components/Shared/HighlightedText";

const breadcrumbs = [
  { title: "Reports", path: "/app/crm/reports/list" },
  { title: "Vendor Shipment Count" },
];



const VendorShipmentCount = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [
    fetchVendorShipmentCounts,
    { data: shipmentCountRes, isLoading, isFetching },
  ] = useLazyGetVendorShipmentCountsQuery();

  const [searchParams] = useSearchParams();

  const keyword = searchParams.get("search") || "";

  const columns = [
    {
      name: "Shop Name",
      selector: (row) => row?.shopName,
      cell: (row) => (
        <HighlightedText text={row?.shopName} highlight={keyword} />
      ),
      sortable: true,
    },
    {
      name: "Owner Name",
      selector: (row) => row?.ownerName,
      cell: (row) => (
        <HighlightedText text={row?.ownerName} highlight={keyword} />
      ),
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row?.email,
      cell: (row) => row?.email || "N/A",
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row?.phone,
      cell: (row) => row?.phone || "N/A",
      sortable: true,
    },
    {
      name: "Address",
      selector: (row) => row?.address,
      cell: (row) => row?.address || "N/A",
      sortable: true,
    },
    {
      name: "Shipment Count",
      selector: (row) => row?.shipmentCount || 0,
      cell: (row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            color: row?.shipmentCount > 0 ? "primary.main" : "text.secondary",
          }}
        >
          {row?.shipmentCount || 0}
        </Typography>
      ),
      sortable: true,
    },
  ];

  useEffect(() => {
    fetchVendorShipmentCounts({
      limit: perPage,
      page: currentPage,
      keyword: keyword,
    });
  }, [currentPage, fetchVendorShipmentCounts, keyword, perPage]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error />
      <BreadcrumbLayout.Paper>
        <TableFilter searchLabel="Shop Name/ Owner Name"></TableFilter>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ flex: 1, justifyContent: "flex-end" }}
            >
              <ExportVendorShipmentCount />
            </Stack>
          </Box>

          {isLoading || isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : shipmentCountRes?.data?.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No data available.
              </Typography>
            </Box>
          ) : (
            <AppTable
              columns={columns}
              data={shipmentCountRes?.data ?? []}
              progressPending={isLoading || isFetching}
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={shipmentCountRes?.totalItems ?? 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
            />
          )}
        </Box>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VendorShipmentCount;